// ============================================================
// UserDetailPage.js
// Route /users/:clientUserId — single ClientUser management
// surface with action buttons (Extend Access, Deactivate,
// Reactivate) and two tabs (Agent Access, History).
//
// URL state:
//   ?tab=agent-access|history  (default: agent-access)
//
// 404 handling: inline not-found state with a return-to-Users
// link rather than a global toast.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Tabs from '../components/Tabs';
import Breadcrumb from '../components/Breadcrumb';
import { RoleBadge, ActiveStatusBadge } from '../components/StatusBadge';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from '../components/Toaster';
import {
  getAdminUserDetail,
  extendAdminUserAccess,
  changeAdminUserRole,
  deactivateAdminUser,
  reactivateAdminUser,
} from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';

// The four core roles an operator may assign (2026-09-09). DSCR identities
// (service_user / representative / external_professional) are grant-
// mediated and deliberately absent; the backend refuses them too.
const CORE_ROLE_KEYS = ['manager', 'carer', 'admin', 'auditor'];
const CORE_ROLE_LABELS = { manager: 'Manager', carer: 'Carer', admin: 'Admin', auditor: 'Auditor' };
import UserAgentAccessTab from './UserAgentAccessTab';
import UserHistoryTab from './UserHistoryTab';
import StatusMessage from '../components/StatusMessage';
import './UserDetailPage.css';

const TAB_IDS = ['agent-access', 'history'];
const DEFAULT_TAB = 'agent-access';

const TAB_DEFS = [
  { id: 'agent-access', label: 'Module Access' },
  { id: 'history',      label: 'History' },
];

const MAX_FUTURE_DAYS = 90;
const DEFAULT_EXTENSION_DAYS = 7;

function isoDateInputValue(d) {
  return d.toISOString().substring(0, 10);
}

function daysFromTodayUtc(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + n);
  return isoDateInputValue(d);
}

export default function UserDetailPage() {
  const { clientUserId: clientUserIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const clientUserId = parseInt(clientUserIdParam, 10);
  const isValidId = Number.isInteger(clientUserId) && clientUserId > 0;

  const rawTab = searchParams.get('tab');
  const activeTab = TAB_IDS.includes(rawTab) ? rawTab : DEFAULT_TAB;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [extendError, setExtendError] = useState(null);
  const [extendSubmitting, setExtendSubmitting] = useState(false);

  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);

  const [reactivating, setReactivating] = useState(false);

  // Change core role (2026-09-09) — the same hand-rolled dialog shape as
  // Extend Access: a <select> plus inline error, not a bare confirm.
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleValue, setRoleValue] = useState('');
  const [roleError, setRoleError] = useState(null);
  const [roleSubmitting, setRoleSubmitting] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!isValidId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await getAdminUserDetail(clientUserId);
      setUser(data);
    } catch (err) {
      if (err && err.status === 404) setNotFound(true);
      else setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientUserId, isValidId]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  function handleTabChange(nextTab) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextTab === DEFAULT_TAB) next.delete('tab'); else next.set('tab', nextTab);
      ['page', 'pageSize', 'action'].forEach((k) => next.delete(k));
      return next;
    });
  }

  function openExtendAccess() {
    // Default new expiry to 7 days after the current expiry, or
    // 7 days from today if no current expiry.
    let defaultIso;
    if (user?.accessExpiresAt) {
      const current = new Date(user.accessExpiresAt);
      const seed = new Date(current.getTime() + DEFAULT_EXTENSION_DAYS * 86400000);
      // Cap at 90 days from today.
      const cap = new Date(Date.now() + MAX_FUTURE_DAYS * 86400000);
      defaultIso = isoDateInputValue(seed > cap ? cap : seed);
    } else {
      defaultIso = daysFromTodayUtc(DEFAULT_EXTENSION_DAYS);
    }
    setExtendDate(defaultIso);
    setExtendError(null);
    setExtendOpen(true);
  }

  function validateExtendDate(value) {
    if (!value) return 'Pick a new expiry date.';
    const d = new Date(`${value}T23:59:59Z`);
    if (Number.isNaN(d.getTime())) return 'Invalid date.';
    const now = new Date();
    if (d <= now) return 'New expiry must be in the future.';
    const cap = new Date(Date.now() + MAX_FUTURE_DAYS * 86400000);
    if (d > cap) return `New expiry must be within ${MAX_FUTURE_DAYS} days from today.`;
    return null;
  }

  async function handleExtendSubmit(e) {
    e.preventDefault();
    const validation = validateExtendDate(extendDate);
    if (validation) {
      setExtendError(validation);
      return;
    }
    setExtendSubmitting(true);
    setExtendError(null);
    try {
      await extendAdminUserAccess(clientUserId, `${extendDate}T23:59:59Z`);
      toast.success('Access extended');
      setExtendOpen(false);
      await fetchUser();
    } catch (err) {
      setExtendError(translateError(err));
    } finally {
      setExtendSubmitting(false);
    }
  }

  function openChangeRole() {
    setRoleValue(user?.roleKey || '');
    setRoleError(null);
    setRoleOpen(true);
  }

  async function handleRoleSubmit(e) {
    e.preventDefault();
    if (!roleValue || roleValue === user?.roleKey) {
      setRoleError('Pick a different role.');
      return;
    }
    setRoleSubmitting(true);
    setRoleError(null);
    try {
      const result = await changeAdminUserRole(clientUserId, roleValue);
      const revoked = Array.isArray(result?.moduleRolesRevoked) ? result.moduleRolesRevoked : [];
      toast.success(revoked.length
        ? `Role changed to ${CORE_ROLE_LABELS[roleValue] || roleValue}. ${revoked.length} module role${revoked.length === 1 ? '' : 's'} revoked.`
        : `Role changed to ${CORE_ROLE_LABELS[roleValue] || roleValue}`);
      setRoleOpen(false);
      await fetchUser();
    } catch (err) {
      setRoleError(translateError(err));
    } finally {
      setRoleSubmitting(false);
    }
  }

  async function handleDeactivateConfirm() {
    setDeactivateSubmitting(true);
    try {
      await deactivateAdminUser(clientUserId);
      toast.success('User deactivated');
      setDeactivateOpen(false);
      await fetchUser();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setDeactivateSubmitting(false);
    }
  }

  async function handleReactivate() {
    setReactivating(true);
    try {
      await reactivateAdminUser(clientUserId);
      toast.success('User reactivated');
      await fetchUser();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setReactivating(false);
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Loading…" />
        <StatusMessage variant="loading" className="user-detail__state">Loading user…</StatusMessage>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageHeader title="User not found" />
        <StatusMessage variant="empty" className="user-detail__state user-detail__state--empty">
          <p>That user does not exist or has been removed.</p>
          <Link to="/users" className="user-detail__back-link">
            ← Return to Users list
          </Link>
        </StatusMessage>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="User" />
        <StatusMessage variant="error" className="user-detail__state user-detail__state--error">
          <p>{error}</p>
          <button type="button" className="user-detail__retry" onClick={fetchUser}>
            Retry
          </button>
        </StatusMessage>
      </>
    );
  }

  const showExtendAccess = user.roleKey === 'auditor' && user.isActive;
  const showDeactivate = user.isActive;
  const showReactivate = !user.isActive;

  // Hierarchical breadcrumb above the page header. When the user's
  // Client has a parent tenant (typical for managed Pathway-1/2
  // clients), the full 6-segment trail is Tenants › {tenantName} ›
  // Clients (filtered to this tenant) › {clientName} › Users › {email}.
  // For Pathway-3 (null tenant) the tenant segment and the
  // Clients-filtered segment collapse to a single unfiltered Clients
  // link. "Users" routes to the Client detail page's Users tab
  // (`?tab=users` is explicit — defensive against any future
  // default-tab change on ClientDetailPage). The existing "Belongs
  // to {tenantName}" line in the meta strip is intentionally
  // retained — the breadcrumb is for navigation; the meta-field
  // communicates the parent relationship as a first-class property
  // of the record. Mirrors the ClientDetailPage breadcrumb decision.
  const breadcrumbItems = [
    { label: 'Tenants', to: '/tenants' },
    ...(user.tenantId != null && user.tenantName
      ? [
          { label: user.tenantName, to: `/tenants/${user.tenantId}` },
          { label: 'Clients',       to: `/clients?tenantId=${user.tenantId}` },
        ]
      : [
          { label: 'Clients', to: '/clients' },
        ]),
    { label: user.clientName, to: `/clients/${user.clientId}` },
    { label: 'Users',         to: `/clients/${user.clientId}?tab=users` },
    { label: user.email },
  ];

  const actions = (
    <div className="user-detail__actions">
      <button
        type="button"
        className="user-detail__action user-detail__action--secondary"
        onClick={openChangeRole}
        data-testid="change-role"
      >
        Change role
      </button>
      {showExtendAccess && (
        <button
          type="button"
          className="user-detail__action user-detail__action--secondary"
          onClick={openExtendAccess}
        >
          Extend Access
        </button>
      )}
      {showDeactivate && (
        <button
          type="button"
          className="user-detail__action user-detail__action--danger"
          onClick={() => setDeactivateOpen(true)}
        >
          Deactivate
        </button>
      )}
      {showReactivate && (
        <button
          type="button"
          className="user-detail__action user-detail__action--secondary"
          onClick={handleReactivate}
          disabled={reactivating}
        >
          {reactivating ? 'Reactivating…' : 'Reactivate'}
        </button>
      )}
    </div>
  );

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <PageHeader
        title={user.email}
        subtitle={user.displayName && user.displayName !== user.email
          ? user.displayName
          : undefined}
        cta={actions}
      />

      <div className="user-detail__meta" data-testid="user-detail-meta">
        <RoleBadge roleKey={user.roleKey} />
        <ActiveStatusBadge isActive={user.isActive} />
        <p className="user-detail__context">
          {user.clientName}
          {user.tenantName
            ? <> · Belongs to <Link to={`/tenants/${user.tenantId}`}>{user.tenantName}</Link></>
            : <> · No tenant (Pathway 3)</>
          }
          {user.createdAt && <> · Created {formatDate(user.createdAt)}</>}
          {user.roleKey === 'auditor' && user.accessExpiresAt && (
            <> · Access expires {formatDate(user.accessExpiresAt)}</>
          )}
        </p>
      </div>

      <Tabs tabs={TAB_DEFS} active={activeTab} onChange={handleTabChange} />

      <div className="user-detail__tab-content">
        {activeTab === 'agent-access' && (
          // Keyed on the role: the module-role panels fetch on mount keyed
          // only on the user id, so a role change must remount them or
          // they keep showing the old role's eligibility.
          <UserAgentAccessTab key={user.roleKey} user={user} onChange={fetchUser} />
        )}
        {activeTab === 'history' && (
          <UserHistoryTab user={user} />
        )}
      </div>

      {extendOpen && (
        <div className="user-detail__dialog-backdrop" role="dialog" aria-modal="true">
          <div className="user-detail__dialog">
            <h2 className="user-detail__dialog-title">Extend access</h2>
            <p className="user-detail__dialog-meta">
              Current access expires {user.accessExpiresAt ? formatDate(user.accessExpiresAt) : '—'}.
            </p>
            <form onSubmit={handleExtendSubmit}>
              <label className="user-detail__dialog-field">
                <span className="user-detail__dialog-label">New expiry</span>
                <input
                  type="date"
                  value={extendDate}
                  onChange={(e) => setExtendDate(e.target.value)}
                  className="user-detail__dialog-input"
                  required
                />
              </label>
              {extendError && (
                <p className="user-detail__dialog-error" role="alert">{extendError}</p>
              )}
              <div className="user-detail__dialog-actions">
                <button
                  type="button"
                  className="user-detail__action user-detail__action--secondary"
                  onClick={() => setExtendOpen(false)}
                  disabled={extendSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="user-detail__action user-detail__action--primary"
                  disabled={extendSubmitting}
                >
                  {extendSubmitting ? 'Extending…' : 'Extend'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {roleOpen && (
        <div className="user-detail__dialog-backdrop" role="dialog" aria-modal="true">
          <div className="user-detail__dialog">
            <h2 className="user-detail__dialog-title">Change role</h2>
            <p className="user-detail__dialog-meta">
              Currently {CORE_ROLE_LABELS[user.roleKey] || user.roleKey}. Module roles the new role cannot hold are revoked automatically and recorded in the history.
            </p>
            <form onSubmit={handleRoleSubmit}>
              <label className="user-detail__dialog-field">
                <span className="user-detail__dialog-label">New role</span>
                <select
                  value={roleValue}
                  onChange={(e) => setRoleValue(e.target.value)}
                  className="user-detail__dialog-input"
                  data-testid="change-role-select"
                >
                  {CORE_ROLE_KEYS.map((k) => (
                    <option key={k} value={k}>{CORE_ROLE_LABELS[k]}</option>
                  ))}
                </select>
              </label>
              {roleValue === 'auditor' && user.roleKey !== 'auditor' && (
                <p className="user-detail__dialog-meta">Auditors are read-only and time-limited. Set an access expiry with Extend Access after the change.</p>
              )}
              {roleError && (
                <p className="user-detail__dialog-error" role="alert">{roleError}</p>
              )}
              <div className="user-detail__dialog-actions">
                <button
                  type="button"
                  className="user-detail__action user-detail__action--secondary"
                  onClick={() => setRoleOpen(false)}
                  disabled={roleSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="user-detail__action user-detail__action--primary"
                  disabled={roleSubmitting}
                  data-testid="change-role-submit"
                >
                  {roleSubmitting ? 'Changing…' : 'Change role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deactivateOpen}
        onClose={() => { if (!deactivateSubmitting) setDeactivateOpen(false); }}
        onConfirm={handleDeactivateConfirm}
        title={`Deactivate ${user.email}?`}
        message="This user will lose access immediately. They can be reactivated later if needed."
        confirmLabel="Deactivate"
        isSubmitting={deactivateSubmitting}
      />
    </>
  );
}

function TabPlaceholder({ label }) {
  return (
    <StatusMessage variant="empty" className="user-detail__state user-detail__state--empty">
      <p>The {label} tab ships in the next commit.</p>
    </StatusMessage>
  );
}

export { TAB_IDS, DEFAULT_TAB };
